import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Zap, LucideIcon, CircleCheck, Wrench, ChevronDown, ChevronRight } from "lucide-react";
import { once } from "events"; // Node-style once()
import VoiceCallInterface from "../voiceCallInterface";
import aposTropheIcon from "@/assets/invertedcommas.svg";
import { NoAgentNotification } from "@/components/ui/NoAgentNotification";
import { RoomEvent, MediaDeviceFailure, Room } from "livekit-client";
import { useCallStatusPolling } from "@/components/hooks/useCallStatusPolling";
import { useLocation } from "react-router-dom";
import { DynamicVariables } from "@/components/ui/DynamicVariables";
import { useDynamicVariablesQuery } from "@/components/hooks/useDynamicVariables";

import {
  LiveKitRoom,
  useVoiceAssistant,
  RoomAudioRenderer,
  AgentState,
  useRoomContext
} from "@livekit/components-react";

interface LivekitVoiceProps {
  pageCategory: string;
  isDialogOpen: boolean;
  toggleDialog: () => void;
  phoneNumber: string;
  callHints: any[];
  callHintsTable: any[];
  agentName: string;
  features: string[];
  icon: string;
  callTitle: string;
  voiceConfig: {
    baseUrl: string;
  url?: string;
  user_id: string;
  agent_id: string;
  overide_previous_context: boolean;
  tenant_id: string;
  workspace_id: string;
  selectedVoiceId?: string; 
  };
  try_speaking: string[] | [];
  activities: string[] | [];
  dynamicVariables: any;
  isFeedbackFormOpen?: boolean;
  ttsConfigId: string | null;
  setIsFeedbackFormOpen?: (isOpen: boolean) => void;
  setConnectionDetails?: (details: any) => void;
}

interface ToolCallPayload {
  functionName: string;
  args: Record<string, unknown>;
  output: Record<string, unknown>;
  isError: boolean;
  durationMs: number;
}

interface TranscriptMessage {
  id: string;
  speaker: string;
  text: string;
  time: string;
  type?: string;
  toolCall?: ToolCallPayload;
  timestamp: number;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface SuggestionItemProps {
  text: string;
  icon?: string;
}

interface HintItemProps {
  text: string;
}

interface FeatureItemProps {
  text: string;
  enabled?: boolean;
}

interface ActivityItemProps {
  text: string;
  icon: LucideIcon;
  status?: "active" | "completed" | "warning";
}

interface TranscriptMessageProps {
  speaker: string;
  text: string;
  toolCall?: ToolCallPayload;
}

// Component implementations with proper typing
const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  className = ""
}) => (
  <div className={`${className}`}>
    <h2 className="text-xl font-normal text-gray-400 mb-8 text-left font-inter">
      {title}
    </h2>
    {children}
  </div>
);

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  text,
  icon = '"'
}) => (
  <div className="flex items-start space-x-2">
    <img src={aposTropheIcon} className="w-4 h-4" />
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const HintItem: React.FC<HintItemProps> = ({ text }) => (
  <div className="flex items-start space-x-3">
    <div className="w-3 h-3 bg-white rounded-full flex-shrink-0 mt-2"></div>
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const FeatureItem: React.FC<FeatureItemProps> = ({ text, enabled = true }) => (
  <div className="flex items-start space-x-3">
    <CircleCheck
      className={`${enabled ? "text-green-400" : "text-gray-500"} w-6 h-6 flex-shrink-0 mt-1`}
    />
    <p
      className={`text-base md:text-xl font-manrope ${enabled ? "text-gray-300" : "text-gray-500"}`}
    >
      {text}
    </p>
  </div>
);

const ActivityItem: React.FC<ActivityItemProps> = ({
  text,
  icon: Icon,
  status = "active"
}) => (
  <div className="flex items-start space-x-4">
    <Icon className="text-gray-400 w-6 h-6 flex-shrink-0 mt-1" />
    <p className="text-gray-300 text-base md:text-xl font-manrope">{text}</p>
  </div>
);

const TranscriptMessage: React.FC<TranscriptMessageProps> = ({
  speaker,
  text,
  toolCall
}) => {
  const [expanded, setExpanded] = useState(false);

  if (speaker === "tool" && toolCall) {
    const Chevron = expanded ? ChevronDown : ChevronRight;
    const statusColor = toolCall.isError ? "text-red-400" : "text-emerald-400";
    return (
      <div className="mb-2 mr-auto max-w-[90%] w-full">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 px-4 py-1 w-full text-left rounded-md hover:bg-white/5 transition-colors"
        >
          <Chevron className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <Wrench className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="font-mono text-sm sm:text-base text-gray-200 truncate">
            {toolCall.functionName}
          </span>
          <span className="text-xs text-gray-500 ml-2">
            {toolCall.durationMs}ms
          </span>
          <span className={`text-xs ml-auto pr-1 ${statusColor}`}>
            {toolCall.isError ? "error" : "ok"}
          </span>
        </button>
        {expanded && (
          <div className="mt-1 ml-6 mr-2 space-y-2">
            <div>
              <div className="text-xs text-gray-500 font-manrope mb-1">args</div>
              <pre className="text-xs text-gray-300 bg-black/40 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words">
                {JSON.stringify(toolCall.args, null, 2)}
              </pre>
            </div>
            <div>
              <div className="text-xs text-gray-500 font-manrope mb-1">output</div>
              <pre className={`text-xs ${toolCall.isError ? "text-red-300" : "text-gray-300"} bg-black/40 rounded p-2 overflow-x-auto whitespace-pre-wrap break-words`}>
                {JSON.stringify(toolCall.output, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`mb-2 ${speaker === "user" ? "ml-auto" : "mr-auto"} max-w-[80%]`}
    >
      <div className={`px-4 py-1`}>
        <div className="flex items-center justify-between mb-1">
          <p
            className={` text-base sm:text-xl leading-relaxed font-manrope ${speaker === "user"
              ? "text-gray-500 text-right ml-auto"
              : "text-white text-left mr-auto"
              }`}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
};

const VoicexCallDashboard: React.FC<LivekitVoiceProps> = ({
  isDialogOpen,
  toggleDialog,
  phoneNumber,
  callHints,
  callHintsTable,
  callTitle,
  agentName,
  ttsConfigId,
  voiceConfig,
  icon,
  features,
  dynamicVariables,
  isFeedbackFormOpen,
  setIsFeedbackFormOpen,
  setConnectionDetails,
  try_speaking,
  activities
}) => {
  type ConnectionDetails = {
    serverUrl: string;
    roomName: string;
    participantName: string;
    participantToken: string;
    voiceConfig: {
      tokenUrl: string;
    };
    ttsConfigId: string;
  };

  const [connectionDetails, updateConnectionDetails] = useState<
    ConnectionDetails | undefined
  >(undefined);
  const [agentState, setAgentState] = useState<AgentState>("disconnected");

  const [transcriptMessages, setTranscriptMessages] = useState<
    TranscriptMessage[]
  >([]);

  const [activeTab, setActiveTab] = useState("transcript");
  const [isCalling, setIsCalling] = useState(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);
  const [currentCallId, setCurrentCallId] = useState<string | undefined>("");
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const transcriptDesktopRef = useRef<HTMLDivElement>(null);
  const transcriptMobileRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);
  const routerLocation = useLocation();
  const [isDynamicVariableSelectionOpen, setIsDynamicVariableSelectionOpen] =
      useState(true);
    const [dynamicVariables1, setDynamicVariables] = useState<Record<string, any>>(
      {}
    );
    const { data: dynamicVarsResponse, isLoading: dynamicVarsLoading } = useDynamicVariablesQuery(voiceConfig, isDynamicVariableSelectionOpen);
    useEffect(() => {
      if (dynamicVarsResponse) {
        console.log("Fetched Dynamic Variables:", dynamicVarsResponse);
      }
    }, [dynamicVarsResponse]);
    
    const handleStartClick = () => {
      setIsDynamicVariableSelectionOpen(true);
    };


  const handleDynamicVariablesSubmit = (variables: Record<string, any>) => {
    setDynamicVariables(variables);
    setIsDynamicVariableSelectionOpen(false);
    connectCall(variables);

  };
  // Poll for call status when serverUrl is empty
  const { data: polledCallDetails } = useCallStatusPolling({
    baseUrl: voiceConfig.baseUrl,
    callId: currentCallId || "",
    userId: voiceConfig.user_id.toString(),
    enabled: shouldPollStatus && !!currentCallId
  });

  const tabs = [
    { id: "transcript", label: "Transcript" },
    { id: "try_speaking", label: "Try Speaking" },
    { id: "call_hints", label: "Call Hints" },
    { id: "features", label: "Features" },
    { id: "activities", label: "Activities" }
  ];


/* THis is done so that any chat widget does not load on the calling interface page */
  useEffect(() => {
    const widget = document.getElementById('nurix-widget');
    if (widget) widget.classList.toggle('hidden', true);

    return ()=>{
      if (widget) widget.classList.toggle('hidden', false);
    }
  }, [routerLocation.pathname]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "transcript":
        return (
          <div className="rounded-lg h-[30vh] overflow-y-auto space-y-2 scroll-smooth">
            {transcriptMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-12 h-12 mb-3 rounded-full bg-gray-800/50 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-medium text-gray-500 mb-1 text-center">
                  Your conversation will appear here
                </h4>
              </div>
            ) : (
              <div className="space-y-3">
                {transcriptMessages.map((message) => (
                  <TranscriptMessage
                    key={message.id}
                    speaker={message.speaker}
                    text={message.text}
                    toolCall={message.toolCall}
                  />
                ))}
              </div>
            )}
            <div ref={transcriptMobileRef} />
          </div>
        );

      case "try_speaking":
        return (
          <div className="space-y-3">
            {try_speaking.map((suggestion, index) => (
              <SuggestionItem key={index} text={suggestion} />
            ))}
          </div>
        );

      case "call_hints":
        return (
          <div className="space-y-3">
            {callHints.map((hint: any, index: number) => (
              <HintItem key={index} text={hint} />
            ))}
          </div>
        );

      case "activities":
        return (
          <div className="space-y-6">
            {activities.map((activity, index) => (
              <ActivityItem
                key={index}
                text={activity}
                icon={Zap}
                status="active"
              />
            ))}
          </div>
        );

      case "features":
        return (
          <div className="space-y-3">
            {features.map((feature, index) => (
              <FeatureItem key={index} text={feature} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const connectCall = async (variables: Record<string, any>) => {
    const response = await fetch(voiceConfig.baseUrl + "/voice/web/call", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "user-id": voiceConfig.user_id.toString()
      },
      body: JSON.stringify({
        agent_id: voiceConfig.agent_id,
        ...(ttsConfigId && { tts_config_id: ttsConfigId }),
        overide_previous_context: voiceConfig.overide_previous_context,
        custom_dynamic_variables_config: variables
      })
    });
    const connectionDetailsData = await response.json();
    
    // Check if serverUrl is empty (call is queued)
    if (!connectionDetailsData.serverUrl) {
      // Call is queued, start polling
      setCurrentCallId(connectionDetailsData.call_id);
      setShouldPollStatus(true);
    } else {
      // Call is ready immediately
      setIsCalling(true);
      updateConnectionDetails({
        ...connectionDetailsData,
        metadata: {
          ttsConfigId: ttsConfigId,
          phoneNumber: phoneNumber
        }
      });
      startTimer();
    }
  };

  const addTranscriptMessage = (
    message: Omit<TranscriptMessage, "id" | "timestamp">
  ) => {
    const newMessage: TranscriptMessage = {
      ...message,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: Date.now()
    };

    setTranscriptMessages((prev) => {
      const updated = [...prev, newMessage];
      return updated;
    });
  };

  useEffect(() => {

    if (isDialogOpen) {
      handleStartClick();
      setTranscriptMessages([]);
    }
    return () => {
      updateConnectionDetails(undefined);
      setTranscriptMessages([]);
      setShouldPollStatus(false);
      setCurrentCallId(undefined);
    };
  }, [isDialogOpen]);

  // Handle polled connection details
  useEffect(() => {
    if (polledCallDetails && polledCallDetails.serverUrl) {
      console.log("Polled connection details received:", polledCallDetails);
      // Stop polling
      setShouldPollStatus(false);
      setIsCalling(true);
      // Update connection details
      updateConnectionDetails({
        ...polledCallDetails,
        metadata: {
          ttsConfigId: ttsConfigId,
          phoneNumber: phoneNumber
        }
      } as any);
      startTimer();
    }
  }, [polledCallDetails, ttsConfigId, phoneNumber]);

  useEffect(() => {
    if (transcriptDesktopRef.current) {
      transcriptDesktopRef.current.scrollIntoView({ behavior: "smooth" });
    }

    if (transcriptMobileRef.current) {
      transcriptMobileRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcriptMessages]);

  const handleEndCall = () => {

  };

  return (
    <div className="min-h-screen bg-black text-white font-manrope">
      {/* Show loading overlay when polling */}
      {shouldPollStatus && !connectionDetails?.serverUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="mb-4">
              <div className="animate-pulse text-2xl font-semibold">
                Finding available agent...
              </div>
            </div>
            <div className="text-gray-400">
              Please wait while we connect you
            </div>
          </div>
        </div>
      )}
      {/* Desktop Layout - Hidden on Mobile */}
      {/* @ts-ignore */}
      <LiveKitRoom
        token={connectionDetails?.participantToken}
        serverUrl={connectionDetails?.serverUrl}
        connect={connectionDetails !== undefined}
        audio={true}
        video={false}
        onMediaDeviceFailure={onDeviceFailure}
        onDisconnected={() => {
          updateConnectionDetails(undefined);
          setShouldPollStatus(false);
          setCurrentCallId(undefined);
        }}
        className="grid grid-rows-[2fr_1fr] items-center"
      >
        <div className="hidden md:flex">
          {/* Left Sidebar */}
          <div className="flex-1 flex flex-col items-center pl-20">
            <div className="flex w-full justify-start mt-10 ">
              <button
                className="w-14 h-14 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                onClick={() => {
                  handleEndCall();
                  if (true) toggleDialog();
                }}
              >
                <X size={30} className="text-gray-300" />
              </button>
            </div>

            <div className="flex flex-col mt-40 space-y-12">
              {try_speaking && try_speaking.length > 0 && (
                <SidebarSection title="Try Speaking">
                  <div className="space-y-3">
                    {try_speaking.map((suggestion, index) => (
                      <SuggestionItem key={index} text={suggestion} />
                    ))}
                  </div>
                </SidebarSection>
              )}

              {callHints && callHints.length > 0 && <SidebarSection title="Call Hints">
                <div className="space-y-3">
                  {callHints.map((hint: any, index: number) => (
                    <HintItem key={index} text={hint} />
                  ))}
                </div>
              </SidebarSection>
              }

              {features && features.length > 0 && <SidebarSection title="Features">
                <div className="space-y-3">
                  {features.map((feature, index) => (
                    <FeatureItem key={index} text={feature} />
                  ))}
                </div>
              </SidebarSection>
              }
            </div>
          </div>

          <div className="flex-1 flex flex-col items-center justify-start mt-12 space-y-16 pr-8">
            <div className="text-center">
              <h1 className="text-3xl font-normal text-gray-300 font-inter">
                {callTitle}
              </h1>
            </div>

            <VoiceAssistantAnimation
              isCalling={isCalling}
              setIsCalling={setIsCalling}
              onStateChange={setAgentState}
              agentName={agentName}
              disconnectCall={() => {
                // setIsFeedbackFormOpen(true);
                toggleDialog();
              }}
              icon={icon}
              onTranscriptMessage={addTranscriptMessage}
              startTimer={startTimer}
              stopTimer={stopTimer}
              seconds={seconds}
              callEnded={callEnded}
              setCallEnded={setCallEnded}
            />

            <RoomAudioRenderer />
            <NoAgentNotification state={agentState} />
          </div>

          {/* Right Sidebar */}
          <div className="flex-1 p-6 justify-start items-center mt-28">
            <div className="flex flex-col space-y-8 pr-24">
              <SidebarSection title="Transcript" className="flex-1">
                <div className="flex flex-col rounded-lg p-4 h-96 overflow-y-auto space-y-2 scroll-smooth">
                  {transcriptMessages.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full">
                      <div className="w-16 h-16 mb-4 rounded-full bg-gray-800/50 flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-xl font-medium text-gray-500 mb-2">
                        Your conversations with {agentName} will appear here
                      </h4>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transcriptMessages.map((message) => (
                        <TranscriptMessage
                          key={message.id}
                          speaker={message.speaker}
                          text={message.text}
                          toolCall={message.toolCall}
                        />
                      ))}
                    </div>
                  )}
                  <div ref={transcriptDesktopRef} />
                </div>
              </SidebarSection>

              {activities && activities.length > 0 && <SidebarSection title="Activities">
                <div className="space-y-6">
                  {activities.map((activity, index) => (
                    <ActivityItem
                      key={index}
                      text={activity}
                      icon={Zap}
                      status="active"
                    />
                  ))}
                </div>
              </SidebarSection>
              }
              {routerLocation.pathname === "/nurix/gsg" && currentCallId && (
                <div className="text-xl font-semibold text-gray-500 font-inter ml-4">
                  Call ID : {currentCallId || "Not Available"}
                </div>
              )}
            </div>
          </div>
        </div>
{isDynamicVariableSelectionOpen && !dynamicVarsLoading && (
        Object.keys(dynamicVarsResponse || {}).length > 0 ? (
          <DynamicVariables
            isOpen={true}
            response={dynamicVarsResponse}
            onClose={() => setIsDynamicVariableSelectionOpen(false)}
            onSubmit={handleDynamicVariablesSubmit}
          />
        ) : (
          (() => {
            setIsDynamicVariableSelectionOpen(false);
            handleDynamicVariablesSubmit({});
            return null;
          })()
        )
      )}
        {/* Mobile Layout - Visible only on Mobile */}
        <div className="md:hidden flex flex-col min-h-screen">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center space-x-4">
              <button
                className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center hover:bg-gray-600 transition-colors"
                onClick={() => {
                  handleEndCall();
                  if (isCalling || callEnded) toggleDialog();
                }}
              >
                <X size={20} className="text-gray-300" />
              </button>
              <h1 className="text-xl font-normal text-gray-300 font-inter">
                {callTitle}
              </h1>
            </div>
          </div>

          <div className="flex items-center justify-center px-4">
            <VoiceAssistantAnimation
              isCalling={isCalling}
              setIsCalling={setIsCalling}
              onStateChange={setAgentState}
              agentName={agentName}
              disconnectCall={() => {
                toggleDialog();
              }}
              icon={icon}
              onTranscriptMessage={() => { }}
              startTimer={startTimer}
              stopTimer={stopTimer}
              seconds={seconds}
              callEnded={callEnded}
              setCallEnded={setCallEnded}
            />
            <NoAgentNotification state={agentState} />
          </div>

          {/* Tab Navigation */}
          <div>
            {/* Tab Headers */}
            <div className="flex-shrink-0 px-12">
              <div className="flex overflow-x-auto scrollbar-hide">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id
                      ? "text-white"
                      : "text-gray-400 hover:text-gray-300"
                      }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 w-[100vw] overflow-y-auto">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </LiveKitRoom>
    </div>
  );
};

function VoiceAssistantAnimation({
  isCalling,
  setIsCalling,
  onStateChange,
  agentName,
  disconnectCall,
  onTranscriptMessage,
  icon,
  startTimer,
  stopTimer,
  seconds,
  callEnded,
  setCallEnded,
}: {
  isCalling: boolean;
  setIsCalling: Function;
  onStateChange: Function;
  agentName: string;
  disconnectCall: Function;
  icon: string;
  onTranscriptMessage: (
    message: Omit<TranscriptMessage, "id" | "timestamp">
  ) => void;
  startTimer: Function;
  stopTimer: Function;
  seconds: number;
  callEnded: boolean;
  setCallEnded: Function;
}) {
  async function waitForConnected(room: Room) {
    if (room.state === "connected") return;
    await once(room, RoomEvent.Connected);
  }

  const { state } = useVoiceAssistant();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const room = useRoomContext();
  const { localParticipant } = useRoomContext();
  const handleChat = () => { };

  // Helper function to format timestamp
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const endCall = async () => {
    if (!isCalling) return;
    await room.disconnect();
    stopTimer();
    setIsCalling(false);
    setCallEnded(true);
  };

  useEffect(() => {
    waitForConnected(room);

    const startRecording = async () => {
      try {
        if (localParticipant) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });

          audioContextRef.current = new AudioContext();
          const source =
            audioContextRef.current.createMediaStreamSource(stream);

          const highPassFilter = audioContextRef.current.createBiquadFilter();
          highPassFilter.type = "highpass";
          highPassFilter.frequency.setValueAtTime(
            400,
            audioContextRef.current.currentTime
          );

          source.connect(highPassFilter);
          const destination =
            audioContextRef.current.createMediaStreamDestination();
          highPassFilter.connect(destination);

          const mediaRecorder = new MediaRecorder(destination.stream);
          setMediaRecorder(mediaRecorder);
          mediaRecorder.start();

          mediaRecorder.onerror = (event) => {
            console.error("MediaRecorder error:", (event as ErrorEvent).error);
          };
        }

        /* @ts-ignore */
        room.on(RoomEvent.DataReceived, (payload, participant, kind) => {
          try {
            const message = new TextDecoder().decode(payload);

            let parsedMessage;
            try {
              parsedMessage = JSON.parse(message);
            } catch (parseError) {
              if (message && message.trim()) {
                const currentTime = Date.now();
                onTranscriptMessage({
                  speaker: "agent",
                  text: message.trim(),
                  time: formatTime(currentTime)
                });
              }
              return;
            }

            // Check if the message has text content and no type (as per requirements)
            if (parsedMessage && parsedMessage.content && !parsedMessage.type) {
              const currentTime = Date.now();
              const speaker = parsedMessage.speaker;

              onTranscriptMessage({
                speaker: speaker,
                text: parsedMessage.content,
                time: formatTime(currentTime),
                type: parsedMessage.type || undefined
              });
            } else if (parsedMessage && parsedMessage.type === "tool_call") {
              // Live tool-call event (LiveToolCallEvent envelope from voiceX
              // TranscriptManager). Surface as a "tool" speaker row so the
              // TranscriptMessage component renders the wrench-icon variant.
              onTranscriptMessage({
                speaker: "tool",
                text: String(parsedMessage.function_name ?? ""),
                time: formatTime(Date.now()),
                type: "tool_call",
                toolCall: {
                  functionName: String(parsedMessage.function_name ?? ""),
                  args: (parsedMessage.args ?? {}) as Record<string, unknown>,
                  output: (parsedMessage.output ?? {}) as Record<string, unknown>,
                  isError: Boolean(parsedMessage.is_error),
                  durationMs: Number(parsedMessage.duration_ms ?? 0),
                },
              });
            } else if (parsedMessage && parsedMessage.text) {
              const currentTime = Date.now();
              onTranscriptMessage({
                speaker: parsedMessage.speaker || "agent",
                text: parsedMessage.content,
                time: formatTime(currentTime),
                type: parsedMessage.type
              });
            }
          } catch (error) {
            console.error("Error processing transcript data:", error);
          }
        });

        room.on(RoomEvent.Disconnected, (reason) => {
          stopTimer();
          setIsCalling(false);
          setCallEnded(true);
          console.log("Call ended - Timer stopped and status updated");
        });
      } catch (error) {
        console.error("Failed to access microphone:", error);
      }
    };

    if (room) {
      startRecording();
    }

    return () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      stopTimer();
    };
  }, []);

  useEffect(() => {
    onStateChange(state);
  }, [state]);

  const toggleMute = useCallback(async () => {
    await waitForConnected(room);

    if (!localParticipant) {
      alert("Agent Disconnedted Please Wait!!");
      return;
    }

    const newEnabledState = !localParticipant.isMicrophoneEnabled;
    await localParticipant.setMicrophoneEnabled(newEnabledState);

    setIsMuted(!newEnabledState);
  }, [room, localParticipant]);

  return (
    <>
      <VoiceCallInterface
        isCalling={isCalling}
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        toggleMute={toggleMute}
        agentName={agentName}
        onEndCall={endCall}
        onChat={handleChat}
        mediaRecorder={mediaRecorder}
        icon={icon}
        seconds={seconds}
        callEnded={callEnded}
      />
    </>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
  console.error(error);
  alert(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
  );
}

export default VoicexCallDashboard;
